import { TrashIcon } from '@heroicons/react/24/solid';
import { RowActionsMenu } from '@src/components/DataTableActions';
import { HiArrowPath, HiHandRaised } from 'react-icons/hi2';
import { ColumnHeaderWithMenu } from '@src/components/Table/components/ColumnHeaderWithMenu';
import UserStatus from './components/UserStatus';
import { SortDirection } from '@src/types/common';
import { ActionGuard } from '@src/components/permissions';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '@src/hooks';
import {
    ACTION_ALLOWED_ROLES,
    MODULE_KEYS,
} from '@src/constants/permissions.const';
import { ManageColumnsDropdown } from '@src/components/Table/components';
import { TFunction } from 'i18next';

export type actionType =
    | 'delete'
    | 'resendInvite'
    | 'changeRole'
    | 'openFilter';

interface RenderColumnsProps {
    t: TFunction;
    onActionClick: (action: actionType, row: any) => void;
    roleId: string | null;
    sorterState?: {
        sortBy: string | null;
        sortOrder: SortDirection | null;
        onSortChange: (columnKey: string, order: SortDirection | null) => void;
    };
    hiddenColumns: string[];
    setHiddenColumns: (keys: string[]) => void;
}

export function RenderColumns({
    t,
    roleId,
    hiddenColumns,
    sorterState,
    setHiddenColumns,
    onActionClick,
}: RenderColumnsProps) {
    const { permissionOf } = usePermissions();

    const baseColumns = [
        {
            key: 'email',
            dataIndex: 'email',
            title: t('pages.users.dataTable.email'),
            sortable: true,
        },
        {
            key: 'accountName',
            dataIndex: 'accountName',
            title: t('pages.users.dataTable.accountName'),
            sortable: true,
        },
        roleId === null
            ? {
                  key: 'roleName',
                  dataIndex: 'roleName',
                  title: t('pages.users.dataTable.role'),
                  sortable: true,
              }
            : null,
        {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: t('pages.users.dataTable.addedAt'),
            sortable: true,
        },
        {
            key: 'status',
            dataIndex: 'status',
            title: t('pages.users.dataTable.status'),
            sortable: true,
        },
    ].filter(Boolean) as any[];

    const canSeeActions =
        (permissionOf(MODULE_KEYS.UserManagement) &&
            ACTION_ALLOWED_ROLES.includes(
                permissionOf(MODULE_KEYS.UserManagement)!
            )) ||
        hiddenColumns.length > 0;

    const actionsColumn = canSeeActions
        ? {
              key: 'actions',
              dataIndex: 'actions',
              className: 'actions-cell',
              title: (
                  <ManageColumnsDropdown
                      t={t}
                      allColumns={baseColumns}
                      hiddenColumns={hiddenColumns}
                      setHiddenColumns={setHiddenColumns}
                  />
              ),
              fixed: 'right',
              render: (_: any, row: any) => {
                  if (row.isMainUser) {
                      return (
                          <div
                              className="flex justify-center items-center text-gray-400"
                              aria-label="Main user - actions locked"
                              title="Main user - actions locked"
                          >
                              <LockClosedIcon width={16} height={16} />
                          </div>
                      );
                  }

                  return (
                      <ActionGuard module="UserManagement">
                          <RowActionsMenu
                              dataRow={row}
                              actions={[
                                  {
                                      key: 'resendInvite',
                                      disabled: row.status === 'joined',
                                      label: t(
                                          'pages.users.dataTable.resendInvite'
                                      ),
                                      icon: (
                                          <HiArrowPath
                                              size={16}
                                              className="w-4 h-4 mr-3"
                                          />
                                      ),
                                      onClick: () =>
                                          onActionClick('resendInvite', row),
                                  },
                                  {
                                      key: 'changeRole',
                                      disabled: row.status === 'waiting',
                                      label: t(
                                          'pages.users.dataTable.changeRole'
                                      ),
                                      icon: (
                                          <HiHandRaised
                                              size={16}
                                              className="w-4 h-4 mr-3"
                                          />
                                      ),
                                      onClick: () =>
                                          onActionClick('changeRole', row),
                                  },
                                  {
                                      key: 'delete',
                                      label: t('dataTableRowActions.delete'),
                                      icon: (
                                          <TrashIcon width={16} height={16} />
                                      ),
                                      onClick: () =>
                                          onActionClick('delete', row),
                                  },
                              ]}
                          />
                      </ActionGuard>
                  );
              },
              width: 1,
          }
        : null;

    const enhancedColumns = [
        ...baseColumns.map((col) => ({
            ...col,
            title: (
                <ColumnHeaderWithMenu
                    title={col.title}
                    columnKey={col.key}
                    allColumns={baseColumns}
                    hiddenColumns={hiddenColumns}
                    setHiddenColumns={setHiddenColumns}
                    sortable={!!col.sortable}
                    sortOrder={
                        sorterState?.sortBy === col.key
                            ? sorterState?.sortOrder
                            : null
                    }
                    onSortChange={(order: SortDirection | null) =>
                        sorterState?.onSortChange(col.key, order)
                    }
                    onColumnMenuClick={(colKey, action) => {
                        switch (action) {
                            case 'hide':
                                if (!hiddenColumns.includes(colKey))
                                    setHiddenColumns([
                                        ...hiddenColumns,
                                        colKey,
                                    ]);
                                break;
                            case 'sortAsc':
                                sorterState?.onSortChange(colKey, 'ASC');
                                break;
                            case 'sortDesc':
                                sorterState?.onSortChange(colKey, 'DESC');
                                break;
                            case 'filter':
                                onActionClick('openFilter', null);
                                break;
                        }
                    }}
                />
            ),
            render: (_, row) => {
                switch (col.key) {
                    case 'roleName':
                        return row.roleName === 'No Role'
                            ? 'Admin'
                            : row.roleName;
                    case 'createdAt':
                        return row.createdAt
                            ? new Date(row.createdAt)
                                  .toLocaleString()
                                  .replace(',', ' -')
                            : '';
                    case 'status':
                        return <UserStatus status={row.status} />;
                    default:
                        return row[col.dataIndex];
                }
            },
        })),
        actionsColumn,
    ];

    return enhancedColumns
        .filter((col) => col !== null)
        .filter((col) => !hiddenColumns.includes(col.key)) as any;
}
